import { create } from 'zustand';
import type { ChatMessage } from './chat.store';

interface Typer {
  userId: string;
  displayName: string;
  profilePictureUrl: string | null;
}

export interface ThreadListItem {
  id: string;
  areaId: string;
  // Null for inline comment threads (design v2) — they hang off a
  // message and have no title. Non-null for standalone titled topics.
  title: string | null;
  author: { userId: string; displayName: string; profilePictureUrl: string | null };
  isResolved: boolean;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  lastMessageAt: string | null;
  unreadCount: number;
}

interface ThreadState {
  threadsByArea: Record<string, ThreadListItem[]>;
  messagesByThread: Record<string, ChatMessage[]>;
  hasMoreByThread: Record<string, boolean>;
  typingByThread: Record<string, Typer[]>;

  setThreads: (areaId: string, threads: ThreadListItem[]) => void;
  addThread: (areaId: string, thread: ThreadListItem) => void;
  updateThread: (threadId: string, updates: { title?: string; isResolved?: boolean; updatedAt?: string }) => void;

  setThreadMessages: (threadId: string, messages: ChatMessage[], hasMore: boolean) => void;
  mergeThreadMessages: (threadId: string, messages: ChatMessage[], hasMore: boolean) => void;
  prependThreadMessages: (threadId: string, messages: ChatMessage[], hasMore: boolean) => void;
  addThreadMessage: (threadId: string, message: ChatMessage) => void;
  addPendingThreadMessage: (threadId: string, message: ChatMessage) => void;
  replacePendingMessage: (tempId: string, message: ChatMessage) => void;
  markPendingFailed: (tempId: string) => void;
  updateMessageContent: (messageId: string, threadId: string, content: string, editedAt: string) => void;
  markMessageDeleted: (messageId: string, threadId: string) => void;
  /** Update fields on a message wherever it lives (events carry messageId, not threadId). */
  updateThreadMessageFields: (messageId: string, fields: Partial<ChatMessage>) => void;
  setTypers: (threadId: string, typers: Typer[]) => void;

  incrementThreadMessageCount: (threadId: string) => void;
  markThreadAsRead: (threadId: string) => void;
  updateThreadLastActivity: (threadId: string, lastMessageAt: string) => void;

  togglePinned: (messageId: string, threadId: string, isPinned: boolean) => void;
  toggleBookmarked: (messageId: string, threadId: string, isBookmarked: boolean) => void;

  addReactionToMessage: (messageId: string, threadId: string, emoji: string, user: { userId: string; displayName: string }, currentUserId: string) => void;
  removeReactionFromMessage: (messageId: string, threadId: string, emoji: string, userId: string, currentUserId: string) => void;

  reset: () => void;
}

export const useThreadStore = create<ThreadState>((set) => ({
  threadsByArea: {},
  messagesByThread: {},
  hasMoreByThread: {},
  typingByThread: {},

  setThreads: (areaId, threads) =>
    set((s) => ({
      threadsByArea: { ...s.threadsByArea, [areaId]: threads },
    })),

  addThread: (areaId, thread) =>
    set((s) => {
      const existing = s.threadsByArea[areaId] ?? [];
      if (existing.some((t) => t.id === thread.id)) return s;
      return {
        threadsByArea: {
          ...s.threadsByArea,
          [areaId]: [thread, ...existing],
        },
      };
    }),

  updateThread: (threadId, updates) =>
    set((s) => {
      const updated: Record<string, ThreadListItem[]> = {};
      for (const [areaId, threads] of Object.entries(s.threadsByArea)) {
        updated[areaId] = threads.map((t) =>
          t.id === threadId
            ? {
                ...t,
                ...(updates.title !== undefined && { title: updates.title }),
                ...(updates.isResolved !== undefined && { isResolved: updates.isResolved }),
                ...(updates.updatedAt !== undefined && { updatedAt: updates.updatedAt }),
              }
            : t,
        );
      }
      return { threadsByArea: updated };
    }),

  setThreadMessages: (threadId, messages, hasMore) =>
    set((s) => ({
      messagesByThread: { ...s.messagesByThread, [threadId]: messages },
      hasMoreByThread: { ...s.hasMoreByThread, [threadId]: hasMore },
    })),

  // Reconnect refetch: server list wins, locally-pending messages survive
  mergeThreadMessages: (threadId, messages, hasMore) =>
    set((s) => {
      const existing = s.messagesByThread[threadId] ?? [];
      const fetchedIds = new Set(messages.map((m) => m.id));
      const pendingLocal = existing.filter(
        (m) => (m.isPending || m.isFailed) && !fetchedIds.has(m.id),
      );
      return {
        messagesByThread: { ...s.messagesByThread, [threadId]: [...messages, ...pendingLocal] },
        hasMoreByThread: { ...s.hasMoreByThread, [threadId]: hasMore },
      };
    }),

  prependThreadMessages: (threadId, messages, hasMore) =>
    set((s) => {
      const existing = s.messagesByThread[threadId] ?? [];
      return {
        messagesByThread: { ...s.messagesByThread, [threadId]: [...messages, ...existing] },
        hasMoreByThread: { ...s.hasMoreByThread, [threadId]: hasMore },
      };
    }),

  addThreadMessage: (threadId, message) =>
    set((s) => {
      const existing = s.messagesByThread[threadId] ?? [];
      if (existing.some((m) => m.id === message.id)) return s;
      return {
        messagesByThread: {
          ...s.messagesByThread,
          [threadId]: [...existing, message],
        },
      };
    }),

  addPendingThreadMessage: (threadId, message) =>
    set((s) => ({
      messagesByThread: {
        ...s.messagesByThread,
        [threadId]: [...(s.messagesByThread[threadId] ?? []), message],
      },
    })),

  replacePendingMessage: (tempId, message) =>
    set((s) => {
      const updated: Record<string, ChatMessage[]> = {};
      for (const [threadId, msgs] of Object.entries(s.messagesByThread)) {
        updated[threadId] = msgs.map((m) =>
          m.tempId === tempId ? { ...message, tempId: undefined, isPending: false, isFailed: false } : m,
        );
      }
      return { messagesByThread: updated };
    }),

  markPendingFailed: (tempId) =>
    set((s) => {
      const updated: Record<string, ChatMessage[]> = {};
      for (const [threadId, msgs] of Object.entries(s.messagesByThread)) {
        updated[threadId] = msgs.map((m) =>
          m.tempId === tempId ? { ...m, isPending: false, isFailed: true } : m,
        );
      }
      return { messagesByThread: updated };
    }),

  updateThreadMessageFields: (messageId, fields) =>
    set((s) => {
      for (const [threadId, msgs] of Object.entries(s.messagesByThread)) {
        if (msgs.some((m) => m.id === messageId)) {
          return {
            messagesByThread: {
              ...s.messagesByThread,
              [threadId]: msgs.map((m) => (m.id === messageId ? { ...m, ...fields } : m)),
            },
          };
        }
      }
      return s;
    }),

  updateMessageContent: (messageId, threadId, content, editedAt) =>
    set((s) => {
      const msgs = s.messagesByThread[threadId];
      if (!msgs) return s;
      return {
        messagesByThread: {
          ...s.messagesByThread,
          [threadId]: msgs.map((m) =>
            m.id === messageId ? { ...m, content, editedAt } : m,
          ),
        },
      };
    }),

  markMessageDeleted: (messageId, threadId) =>
    set((s) => {
      const msgs = s.messagesByThread[threadId];
      if (!msgs) return s;
      return {
        messagesByThread: {
          ...s.messagesByThread,
          [threadId]: msgs.map((m) =>
            m.id === messageId ? { ...m, isDeleted: true, content: null } : m,
          ),
        },
      };
    }),

  setTypers: (threadId, typers) =>
    set((s) => ({
      typingByThread: { ...s.typingByThread, [threadId]: typers },
    })),

  incrementThreadMessageCount: (threadId) =>
    set((s) => {
      const updated: Record<string, ThreadListItem[]> = {};
      for (const [areaId, threads] of Object.entries(s.threadsByArea)) {
        updated[areaId] = threads.map((t) =>
          t.id === threadId ? { ...t, messageCount: t.messageCount + 1, unreadCount: t.unreadCount + 1 } : t,
        );
      }
      return { threadsByArea: updated };
    }),

  markThreadAsRead: (threadId) =>
    set((s) => {
      const updated: Record<string, ThreadListItem[]> = {};
      for (const [areaId, threads] of Object.entries(s.threadsByArea)) {
        updated[areaId] = threads.map((t) =>
          t.id === threadId ? { ...t, unreadCount: 0 } : t,
        );
      }
      return { threadsByArea: updated };
    }),

  updateThreadLastActivity: (threadId, lastMessageAt) =>
    set((s) => {
      const updated: Record<string, ThreadListItem[]> = {};
      for (const [areaId, threads] of Object.entries(s.threadsByArea)) {
        updated[areaId] = threads.map((t) =>
          t.id === threadId ? { ...t, lastMessageAt } : t,
        );
      }
      return { threadsByArea: updated };
    }),

  togglePinned: (messageId, threadId, isPinned) =>
    set((s) => {
      const msgs = s.messagesByThread[threadId];
      if (!msgs) return s;
      return {
        messagesByThread: {
          ...s.messagesByThread,
          [threadId]: msgs.map((m) =>
            m.id === messageId ? { ...m, isPinned } : m,
          ),
        },
      };
    }),

  toggleBookmarked: (messageId, threadId, isBookmarked) =>
    set((s) => {
      const msgs = s.messagesByThread[threadId];
      if (!msgs) return s;
      return {
        messagesByThread: {
          ...s.messagesByThread,
          [threadId]: msgs.map((m) =>
            m.id === messageId ? { ...m, isBookmarked } : m,
          ),
        },
      };
    }),

  addReactionToMessage: (messageId, threadId, emoji, user, currentUserId) =>
    set((s) => {
      const msgs = s.messagesByThread[threadId];
      if (!msgs) return s;
      return {
        messagesByThread: {
          ...s.messagesByThread,
          [threadId]: msgs.map((m) => {
            if (m.id !== messageId) return m;
            const existing = m.reactions ?? [];
            const idx = existing.findIndex((r) => r.emoji === emoji);
            if (idx >= 0) {
              const r = existing[idx];
              if (r.users.some((u) => u.userId === user.userId)) return m;
              const updated = [...existing];
              updated[idx] = { ...r, count: r.count + 1, users: [...r.users, user], includesMe: r.includesMe || user.userId === currentUserId };
              return { ...m, reactions: updated };
            }
            return { ...m, reactions: [...existing, { emoji, count: 1, users: [user], includesMe: user.userId === currentUserId }] };
          }),
        },
      };
    }),

  removeReactionFromMessage: (messageId, threadId, emoji, userId, currentUserId) =>
    set((s) => {
      const msgs = s.messagesByThread[threadId];
      if (!msgs) return s;
      return {
        messagesByThread: {
          ...s.messagesByThread,
          [threadId]: msgs.map((m) => {
            if (m.id !== messageId) return m;
            const existing = m.reactions ?? [];
            const idx = existing.findIndex((r) => r.emoji === emoji);
            if (idx < 0) return m;
            const r = existing[idx];
            if (r.count <= 1) {
              return { ...m, reactions: existing.filter((_, i) => i !== idx) };
            }
            const updated = [...existing];
            const newUsers = r.users.filter((u) => u.userId !== userId);
            updated[idx] = { ...r, count: r.count - 1, users: newUsers, includesMe: userId === currentUserId ? false : r.includesMe };
            return { ...m, reactions: updated };
          }),
        },
      };
    }),

  reset: () =>
    set({
      threadsByArea: {},
      messagesByThread: {},
      hasMoreByThread: {},
      typingByThread: {},
    }),
}));
